import type { IncidentReport } from '@lmc/ts-common'
import dayjs, { type Dayjs } from 'dayjs'
import { getCombinedTimeIso } from '../../../utils/incidentUtils'
import type { IncidentCaseV2StepValues } from './incidentCaseV2Steps'

export type NotifiedPartyValue = NonNullable<
  IncidentCaseV2StepValues['notifiedParties']
>[number]

/**
 * A date-only answer (a due date, a date shared) as the backend stores it:
 * midnight UTC of the day picked, whatever the browser's timezone.
 */
export const toDateOnlyIso = (date: Dayjs | null | undefined): string | null =>
  date ? `${date.format('YYYY-MM-DD')}T00:00:00.000Z` : null

/**
 * A saved date-only answer back as the calendar day it was saved for. Read from
 * the date part, never as an instant: midnight UTC is the previous evening west
 * of UTC, so `dayjs(iso)` would show the day before there.
 */
export const fromDateOnlyIso = (
  iso: string | null | undefined
): Dayjs | null => (iso ? dayjs(iso.slice(0, 10)) : null)

/**
 * A date and a time picker as one instant. While both still hold the instant
 * they were filled from, that instant goes back exactly. Combining them again
 * reads the wall-clock time in the browser's zone, which drops milliseconds
 * and, in the hour the clocks go back, picks the earlier (BST) 01:00-02:00.
 * A time the user picks in that hour still reads as BST: the picker can't say
 * which one they meant.
 */
export const toInstantIso = (
  date: Dayjs | null | undefined,
  time: Dayjs | null | undefined,
  filledFrom: Date | string | null | undefined
): string | null => {
  if (!date || !time) {
    return null
  }
  const original = filledFrom ? dayjs(filledFrom) : null
  return original?.isSame(date) && original.isSame(time)
    ? original.toISOString()
    : getCombinedTimeIso(date, time)
}

/** Nothing recorded: no value, an empty list or only spaces. */
export const isBlank = (value: unknown) =>
  value === undefined ||
  value === null ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'string' && value.trim() === '')

/** Trimmed text, or nothing when blank. */
export const trimmed = (text: string | null | undefined): string | undefined =>
  text?.trim() || undefined

/** `object` without its blank fields; the read leaves those out too. */
export const withoutBlanks = <T extends object>(object: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => !isBlank(value))
  ) as Partial<T>

/** A yes/no answer with details under No, as saved; nothing while unanswered. */
export const toAnswered = <Yes extends object>(
  hasBeenApplied: boolean | undefined,
  details: string | undefined,
  yes: Yes
) => {
  if (hasBeenApplied === undefined) {
    return undefined
  }
  return hasBeenApplied
    ? { hasBeenApplied, ...yes }
    : { hasBeenApplied, ...withoutBlanks({ details: trimmed(details) }) }
}

/** Named rows with the blank ones dropped and the names trimmed. */
export const toNamedPeople = (
  rows: ReadonlyArray<{ name?: string | null }> | null | undefined
): Array<{ name: string }> =>
  (rows ?? [])
    .map((row) => (row.name ?? '').trim())
    .filter((name) => name !== '')
    .map((name) => ({ name }))

/**
 * The case's saved notified parties, ids included so a save keeps them. Two
 * steps write this list; a save replaces only the parties its step owns.
 */
export const savedNotifiedParties = (
  incidentReport: IncidentReport
): Array<NotifiedPartyValue> =>
  // `notifiedParties` is not on the ts-common case type.
  (
    incidentReport.case as {
      notifiedParties?: Array<NotifiedPartyValue>
    } | null
  )?.notifiedParties ?? []

/** A notified party's answers, in a form (dates as pickers' values) or as saved. */
type NotifiedPartyDetail = Omit<
  Partial<NotifiedPartyValue>,
  'enquiryClosedAt' | 'notifiedAt'
> & {
  enquiryClosedAt?: unknown
  notifiedAt?: unknown
}

/** Whether anything is recorded about a safeguarding enquiry. */
export const hasEnquiryDetail = (
  detail: NotifiedPartyDetail | undefined
): boolean =>
  Boolean(
    detail?.enquiryClosedAt ||
      trimmed(detail?.enquiryLocalAuthority) ||
      detail?.enquiryOutcome ||
      trimmed(detail?.enquiryOutcomeAndRecommendation) ||
      trimmed(detail?.enquiryType)
  )

/** Whether anything is recorded against a notified party, in a form or as saved. */
export const hasNotifiedPartyDetail = (
  detail: NotifiedPartyDetail | undefined
): boolean =>
  Boolean(
    trimmed(detail?.details) ||
      detail?.notifiedAt ||
      detail?.notifiedBy ||
      detail?.progressedToEnquiry ||
      trimmed(detail?.referenceNumber) ||
      hasEnquiryDetail(detail)
  )
